import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useVisibilityPolling } from './useVisibilityPolling';

/**
 * Unified Balance Hook (MongoDB version - API polling)
 */
export const useUnifiedBalance = () => {
  const { user } = useAuth();
  const [balanceData, setBalanceData] = useState({
    totalBalance: 0,
    availableBalance: 0,
    lockedBalance: 0,
    holdings: [],
    portfolioBreakdown: [],
    prices: {},
    holdingsCount: 0,
    loading: true,
    error: null,
    lastUpdated: null
  });

  const fetchBalance = useCallback(async () => {
    if (!user?.id) {
      setBalanceData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const response = await api.get('/balance');
      const data = response.data;

      const formattedHoldings = (data.holdings || []).map(h => ({
        crypto_symbol: h.crypto_symbol,
        crypto_name: h.crypto_name,
        crypto_id: h.crypto_symbol?.toLowerCase() || '',
        amount: h.amount || 0,
        balance: h.amount || 0,
        available_balance: h.available_amount || h.amount || 0,
        locked_balance: h.locked_amount || 0,
        current_value: h.value_usdt || 0,
        usdt_value: h.value_usdt || 0,
        available_usdt: h.available_usdt || 0,
        locked_usdt: h.locked_usdt || 0,
        price: h.price_usdt || data.prices?.[h.crypto_symbol] || 0,
        percentage: 0
      }));

      if (data.portfolio_breakdown) {
        data.portfolio_breakdown.forEach(pb => {
          const holding = formattedHoldings.find(h => h.crypto_symbol === pb.crypto_symbol);
          if (holding) holding.percentage = pb.percentage || 0;
        });
      }

      setBalanceData({
        totalBalance: data.balance || 0,
        availableBalance: data.available_balance || 0,
        lockedBalance: data.locked_balance || 0,
        holdings: formattedHoldings,
        portfolioBreakdown: data.portfolio_breakdown || [],
        holdingsCount: data.holdings_count || formattedHoldings.length,
        prices: data.prices || {},
        loading: false,
        error: null,
        lastUpdated: new Date(data.last_updated || Date.now())
      });
    } catch (error) {
      try {
        const fallbackResponse = await api.get('/wallet/balance');
        const fallbackData = fallbackResponse.data;
        
        const formattedHoldings = (fallbackData.holdings || []).map(h => ({
          crypto_symbol: h.crypto_symbol || h.symbol,
          crypto_id: (h.crypto_symbol || '').toLowerCase(),
          amount: h.amount || h.balance || 0,
          balance: h.amount || h.balance || 0,
          available_balance: h.available || h.available_balance || h.amount || 0,
          locked_balance: h.locked || h.locked_balance || 0,
          current_value: h.current_value || h.usdt_value || 0,
          usdt_value: h.current_value || h.usdt_value || 0,
          price: h.price || fallbackData.prices?.[h.crypto_symbol] || 0
        }));

        setBalanceData({
          totalBalance: fallbackData.balance || 0,
          availableBalance: fallbackData.available_balance || 0,
          lockedBalance: fallbackData.locked_balance || 0,
          holdings: formattedHoldings,
          portfolioBreakdown: [],
          holdingsCount: formattedHoldings.length,
          prices: fallbackData.prices || {},
          loading: false,
          error: null,
          lastUpdated: new Date()
        });
      } catch (fallbackError) {
        setBalanceData(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Failed to fetch balance'
        }));
      }
    }
  }, [user?.id]);

  const syncBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      await api.post('/balance/sync');
      await fetchBalance();
    } catch (error) {
      console.error('Error syncing balance:', error);
    }
  }, [user?.id, fetchBalance]);

  useVisibilityPolling(fetchBalance, 15000, !!user?.id);

  // Instant refresh on WebSocket events pushed from admin actions or trade settlement
  useEffect(() => {
    if (!user?.id) return undefined;
    const handler = () => fetchBalance();
    // These events are dispatched by useUserWebSocket.js upon server-side broadcasts
    const events = [
      'phantomtrades:balance-changed',
      'phantomtrades:trade-settled',
      'phantomtrades:deposit-processed',
      'phantomtrades:withdrawal-processed',
    ];
    events.forEach(ev => window.addEventListener(ev, handler));
    return () => events.forEach(ev => window.removeEventListener(ev, handler));
  }, [user?.id, fetchBalance]);

  useEffect(() => {
    if (!user?.id) {
      setBalanceData(prev => ({ ...prev, loading: false }));
    }
  }, [user?.id]);

  const refresh = useCallback(async () => {
    setBalanceData(prev => ({ ...prev, loading: true }));
    await fetchBalance();
  }, [fetchBalance]);

  return {
    ...balanceData,
    refresh,
    syncBalance,
    getHoldingBySymbol: (symbol) => balanceData.holdings.find(h => h.crypto_symbol === symbol),
    getHoldingValue: (symbol) => {
      const holding = balanceData.holdings.find(h => h.crypto_symbol === symbol);
      return holding?.usdt_value || 0;
    },
    getTotalInCrypto: (symbol) => {
      const price = balanceData.prices[symbol];
      if (!price || price === 0) return 0;
      return balanceData.totalBalance / price;
    }
  };
};

export default useUnifiedBalance;
